import React, { useState } from 'react';
import { Modal, Header, Icon, Button } from 'semantic-ui-react';
import { CompanyDataViewerProps, TabType } from './types';
import { DesignSystem } from '../../styles/designSystem';
import { formatCnpj } from './utils';

// Importando as abas
import BasicDataTab from './tabs/BasicDataTab';
import AddressTab from './tabs/AddressTab';
import ActivitiesTab from './tabs/ActivitiesTab';
import PartnersTab from './tabs/PartnersTab';
import FiscalTab from './tabs/FiscalTab';

const CompanyDataViewer: React.FC<CompanyDataViewerProps> = ({
  data,
  isOpen,
  onClose,
  onConfirm,
  mode = 'view',
  title = 'Dados da Empresa'
}) => {
  const [activeTab, setActiveTab] = useState<TabType>('dados-basicos');

  // Configuração das abas
  const tabs = [
    { key: 'dados-basicos', icon: 'info circle', label: 'Dados Básicos' },
    { key: 'endereco', icon: 'map marker alternate', label: 'Endereço' },
    { key: 'atividades', icon: 'briefcase', label: 'Atividades' },
    { key: 'socios', icon: 'users', label: 'Sócios/QSA' },
    { key: 'fiscal', icon: 'calculator', label: 'Dados Fiscais' }
  ];

  // Renderiza o conteúdo da aba ativa
  const renderTabContent = () => {
    if (!data) return null;

    switch (activeTab) {
      case 'dados-basicos':
        return <BasicDataTab data={data} />;
      case 'endereco':
        return <AddressTab data={data} />;
      case 'atividades':
        return <ActivitiesTab data={data} />;
      case 'socios':
        return <PartnersTab data={data} />;
      case 'fiscal':
        return <FiscalTab data={data} />;
      default:
        return <BasicDataTab data={data} />;
    }
  };

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      size="large"
      closeIcon
      style={{
        background: DesignSystem.colors.lightPurple.gradient,
        borderRadius: DesignSystem.borderRadius.large,
        boxShadow: DesignSystem.shadows.modal,
        maxHeight: '90vh',
        margin: '2vh auto'
      }}
    >
      <Modal.Header
        style={{
          background: DesignSystem.colors.lightPurple.gradient,
          color: 'white',
          border: 'none',
          borderRadius: DesignSystem.components.modal.header.borderRadius,
          padding: '1rem 1.5rem',
          boxShadow: DesignSystem.components.modal.header.boxShadow,
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          position: 'relative',
          zIndex: 2
        }}>
          <div style={{
            background: 'rgba(255,255,255,0.2)',
            borderRadius: '50%',
            padding: '0.3rem',
            backdropFilter: 'blur(10px)'
          }}>
            <Icon name="building" />
          </div>
          <div>
            <Header as="h2" style={{ color: 'white', margin: 0, fontWeight: '600', fontSize: '1.2rem' }}>
              {title}
            </Header>
            {data?.cnpj && (
              <p style={{ color: 'rgba(255,255,255,0.8)', margin: '0.1rem 0 0 0', fontSize: '0.8rem' }}>
                CNPJ: {formatCnpj(data.cnpj)}
              </p>
            )}
          </div>
        </div>

        {/* Elemento decorativo */}
        <div style={{
          position: 'absolute',
          top: '-50%',
          right: '-10%',
          width: '200px',
          height: '200px',
          background: 'rgba(255,255,255,0.1)',
          borderRadius: '50%',
          zIndex: 1
        }} />
      </Modal.Header>

      <Modal.Content
        scrolling
        style={{
          background: DesignSystem.components.modal.content.background,
          padding: '0',
          borderRadius: DesignSystem.components.modal.content.borderRadius,
          maxHeight: '70vh'
        }}
      >
        {data && (
          <div>
            {/* Barra de navegação moderna */}
            <div style={{
              background: 'white',
              padding: '0.75rem 1rem',
              borderBottom: '1px solid #e9ecef',
              boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
              position: 'sticky',
              top: 0,
              zIndex: 10
            }}>
              <Header as="h3" style={{
                color: '#2c3e50',
                marginBottom: '0.5rem',
                fontWeight: '600',
                textAlign: 'center',
                fontSize: '0.95rem'
              }}>
                <Icon name="chart bar" />
                Seções do Cliente
              </Header>

              <div style={{
                display: 'flex',
                justifyContent: 'center',
                gap: '0.3rem',
                flexWrap: 'wrap'
              }}>
                {tabs.map((tab) => (
                  <Button
                    key={tab.key}
                    onClick={() => setActiveTab(tab.key as TabType)}
                    style={{
                      background: activeTab === tab.key
                        ? DesignSystem.colors.secondary.gradient
                        : 'white',
                      color: activeTab === tab.key ? 'white' : '#666',
                      border: `1px solid ${activeTab === tab.key ? '#4A90E2' : '#e9ecef'}`,
                      borderRadius: DesignSystem.borderRadius.large,
                      padding: '0.4rem 0.8rem',
                      fontWeight: DesignSystem.typography.fontWeight.semibold,
                      fontSize: DesignSystem.typography.fontSize.sm,
                      boxShadow: activeTab === tab.key
                        ? DesignSystem.shadows.blueAccent
                        : DesignSystem.shadows.subtle,
                      transition: DesignSystem.transitions.normal,
                      transform: activeTab === tab.key ? 'translateY(-1px)' : 'translateY(0)',
                      minWidth: '85px'
                    }}
                  >
                    <Icon name={tab.icon as any} />
                    {tab.label}
                  </Button>
                ))}
              </div>
            </div>

            {/* Conteúdo das abas */}
            {renderTabContent()}
          </div>
        )}
      </Modal.Content>

      <Modal.Actions style={{
        background: DesignSystem.components.modal.actions.background,
        borderTop: '1px solid #e9ecef',
        padding: '1rem 1.5rem',
        justifyContent: 'center',
        gap: '0.5rem',
        borderRadius: DesignSystem.components.modal.actions.borderRadius
      }}>
        <Button
          onClick={onClose}
          style={DesignSystem.components.button.secondary}
          onMouseEnter={(e: any) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,0,0,0.15)';
          }}
          onMouseLeave={(e: any) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 4px 15px rgba(0,0,0,0.1)';
          }}
        >
          <Icon name="cancel" />
          {mode === 'create' ? 'Cancelar' : 'Fechar'}
        </Button>

        {mode === 'create' && onConfirm && (
          <Button
            primary
            onClick={() => onConfirm(data!)}
            style={{
              background: DesignSystem.colors.lightPurple.gradient,
              color: 'white',
              border: 'none',
              borderRadius: DesignSystem.borderRadius.round,
              padding: '0.6rem 1.5rem',
              fontWeight: DesignSystem.typography.fontWeight.semibold,
              fontSize: DesignSystem.typography.fontSize.md,
              boxShadow: DesignSystem.shadows.blueAccent,
              transition: DesignSystem.transitions.normal
            }}
            onMouseEnter={(e: any) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 15px 35px rgba(176, 196, 222, 0.3)';
            }}
            onMouseLeave={(e: any) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 8px 25px rgba(176, 196, 222, 0.2)';
            }}
          >
            <Icon name="check" />
            Confirmar e Criar Cliente
          </Button>
        )}
      </Modal.Actions>
    </Modal>
  );
};

export default CompanyDataViewer;
